// src/pages/AcceptPartnerInvite.jsx
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  LogOut,
  Mail,
  Send,
  ShieldCheck,
  UserCog,
  XCircle,
} from "lucide-react";
import {
  useLocation,
} from "react-router-dom";
import { supabase } from "../../supabaseClient";
import AddTeamMemberModal from "../../components/partner/AddTeamMemberModal";
import {
  acceptPartnerMemberInvitation,
  getPartnerMemberInviteContext,
} from "../../lib/partner/partnerMemberInvitations";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Administrator",
  cohort_manager:
    "Cohort manager",
  facilitator: "Facilitator",
  viewer: "Viewer",
};

function saveToken(token) {
  if (!token) return;

  sessionStorage.setItem(
    "pendingPartnerMemberInviteToken",
    token
  );
}

function clearStoredToken() {
  sessionStorage.removeItem(
    "pendingPartnerMemberInviteToken"
  );
}

function cleanBrowserUrl(token) {
  const nextUrl =
    `/accept-partner-invite` +
    `?token=${encodeURIComponent(
      token
    )}`;

  window.history.replaceState(
    {},
    document.title,
    nextUrl
  );
}

async function establishSessionFromUrl() {
  const params =
    new URLSearchParams(
      window.location.search
    );

  const code =
    params.get("code");

  if (code) {
    const {
      data,
      error,
    } =
      await supabase.auth
        .exchangeCodeForSession(
          code
        );

    if (error) throw error;

    return data?.session || null;
  }

  const hashParams =
    new URLSearchParams(
      String(
        window.location.hash || ""
      ).replace(/^#/, "")
    );

  const accessToken =
    hashParams.get(
      "access_token"
    );

  const refreshToken =
    hashParams.get(
      "refresh_token"
    );

  if (
    accessToken &&
    refreshToken
  ) {
    const {
      data,
      error,
    } =
      await supabase.auth
        .setSession({
          access_token:
            accessToken,

          refresh_token:
            refreshToken,
        });

    if (error) throw error;

    return data?.session || null;
  }

  const {
    data,
    error,
  } =
    await supabase.auth
      .getSession();

  if (error) throw error;

  return data?.session || null;
}

function getPasswordIssues(
  password = ""
) {
  const issues = [];

  if (password.length < 8) {
    issues.push(
      "at least 8 characters"
    );
  }

  if (!/[a-z]/.test(password)) {
    issues.push(
      "a lowercase letter"
    );
  }

  if (!/[A-Z]/.test(password)) {
    issues.push(
      "an uppercase letter"
    );
  }

  if (!/[0-9]/.test(password)) {
    issues.push("a number");
  }

  if (
    !/[^A-Za-z0-9]/.test(
      password
    )
  ) {
    issues.push(
      "a special character"
    );
  }

  return issues;
}

function getUserFullName(user) {
  const metadata =
    user?.user_metadata || {};

  return String(
    metadata.full_name ||
      metadata.name ||
      metadata.display_name ||
      ""
  ).trim();
}

function continueToPartnerPortal() {
  window.location.replace(
    "/partner"
  );
}

export default function AcceptPartnerInvite() {
  const location =
    useLocation();

  const processed =
    useRef(false);

  const redirectTimer =
    useRef(null);

  const token =
    useMemo(() => {
      const params =
        new URLSearchParams(
          location.search
        );

      const urlToken =
        params.get("token");

      if (urlToken) {
        saveToken(urlToken);

        return urlToken;
      }

      return (
        sessionStorage.getItem(
          "pendingPartnerMemberInviteToken"
        ) || ""
      );
    }, [location.search]);

  const [context, setContext] =
    useState(null);

  const [currentUser, setCurrentUser] =
    useState(null);

  const [
    signedInEmail,
    setSignedInEmail,
  ] = useState("");

  const [
  fullName,
  setFullName,
] = useState("");



  const [phase, setPhase] =
    useState("loading");

  const [error, setError] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [
    sendingLoginLink,
    setSendingLoginLink,
  ] = useState(false);

  const [
    loginLinkSent,
    setLoginLinkSent,
  ] = useState(false);

  const [password, setPassword] =
    useState("");

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState("");

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const [
    savingPassword,
    setSavingPassword,
  ] = useState(false);

  const [
  teamModalOpen,
  setTeamModalOpen,
] = useState(false);

const [
  invitedTeamMembers,
  setInvitedTeamMembers,
] = useState([]);

  function finishOnboarding() {
  setTeamModalOpen(false);
  setError("");

  clearStoredToken();

  setPhase(
    "success"
  );

  scheduleRedirect();
}

function handleAddTeamMember() {
  if (!context?.partner_id) {
    setError(
      "Your partner organisation could not be loaded. Please refresh the page and try again."
    );

    return;
  }

  setError("");
  setTeamModalOpen(true);
}

async function handleTeamMemberInvited(
  result,
  submittedMember
) {

  const invitedName =
  String(
    result?.name ||
      submittedMember?.name ||
      ""
  )
    .trim()
    .replace(/\s+/g, " ");
  const invitedEmail =
    String(
      result?.email ||
        submittedMember?.email ||
        ""
    )
      .trim()
      .toLowerCase();

  const invitedRole =
    String(
      result?.role ||
        submittedMember?.role ||
        ""
    )
      .trim()
      .toLowerCase();

  setInvitedTeamMembers(
    (current) => {
      const withoutExisting =
        current.filter(
          (member) =>
            member.email !==
            invitedEmail
        );

      return [
  ...withoutExisting,
  {
    invitationId:
      result?.invitationId ||
      result?.invitation_id ||
      invitedEmail,

    name:
      invitedName,

    email:
      invitedEmail,

    role:
      invitedRole,
  },
];
    }
  );

  setTeamModalOpen(false);

  setPhase(
    "team-summary"
  );
}


  function scheduleRedirect() {
    if (
      redirectTimer.current
    ) {
      window.clearTimeout(
        redirectTimer.current
      );
    }

    redirectTimer.current =
      window.setTimeout(() => {
        continueToPartnerPortal();
      }, 2000);
  }

  useEffect(() => {
    if (
      processed.current
    ) {
      return undefined;
    }

    processed.current = true;

    async function processInvitation() {
      try {
        setPhase("loading");
        setError("");

        if (!token) {
          throw new Error(
            "The partner invitation token is missing."
          );
        }

        const inviteContext =
          await getPartnerMemberInviteContext(
            token
          );

        setContext(
          inviteContext
        );

        if (
          inviteContext.status ===
            "revoked" ||
          inviteContext.status ===
            "expired"
        ) {
          setPhase(
            "unavailable"
          );

          return;
        }

        if (
          inviteContext.status ===
            "pending" &&
          !inviteContext.can_accept
        ) {
          setPhase(
            "unavailable"
          );

          return;
        }

        let session = null;

        try {
          session =
            await establishSessionFromUrl();

          if (session) {
            cleanBrowserUrl(
              token
            );
          }
        } catch (sessionError) {
          console.warn(
            "Partner invitation authentication could not be completed:",
            sessionError
          );
        }

        if (!session?.user) {
          setPhase(
            "authentication-required"
          );

          return;
        }

        setCurrentUser(
          session.user
        );

        setSignedInEmail(
          session.user.email || ""
        );

        setFullName(
          getUserFullName(
            session.user
          )
        );

        setPhase(
  "account-setup"
);

        return;
      } catch (processError) {
        console.error(
          "Accepting partner team invitation failed:",
          processError
        );

        setError(
          processError?.message ||
            "The partner invitation could not be accepted."
        );

        setPhase("error");
      }
    }

    processInvitation();

    return () => {
      if (
        redirectTimer.current
      ) {
        window.clearTimeout(
          redirectTimer.current
        );
      }
    };

    // Process once for the current invite page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSendLoginLink(
    event
  ) {
    event.preventDefault();

    try {
      setSendingLoginLink(true);
      setLoginLinkSent(false);
      setError("");

      const cleanEmail =
        String(email || "")
          .trim()
          .toLowerCase();

      if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          cleanEmail
        )
      ) {
        throw new Error(
          "Enter the email address that received the invitation."
        );
      }

      const redirectTo =
        `${window.location.origin}` +
        `/accept-partner-invite` +
        `?token=${encodeURIComponent(
          token
        )}`;

      const {
        error: otpError,
      } =
        await supabase.auth
          .signInWithOtp({
            email: cleanEmail,

            options: {
              shouldCreateUser:
                false,

              emailRedirectTo:
                redirectTo,
            },
          });

      if (otpError) {
        throw otpError;
      }

      setLoginLinkSent(true);
    } catch (sendError) {
      console.error(
        "Sending partner invitation sign-in link failed:",
        sendError
      );

      setError(
        sendError?.message ||
          "The sign-in link could not be sent."
      );
    } finally {
      setSendingLoginLink(
        false
      );
    }
  }

  

  async function handleAccountSetup(
  event
) {
  event.preventDefault();

  try {
    setSavingPassword(true);
    setError("");

    const cleanFullName =
      String(fullName || "")
        .trim()
        .replace(/\s+/g, " ");

    if (!cleanFullName) {
      throw new Error(
        "Please enter your full name."
      );
    }

    if (
      cleanFullName.length >
      100
    ) {
      throw new Error(
        "Your full name cannot exceed 100 characters."
      );
    }

    const issues =
      getPasswordIssues(
        password
      );

    if (
      issues.length > 0
    ) {
      throw new Error(
        `Your password still needs ${issues.join(
          ", "
        )}.`
      );
    }

    if (
      password !==
      confirmPassword
    ) {
      throw new Error(
        "The passwords do not match."
      );
    }

    const existingMetadata =
      currentUser
        ?.user_metadata ||
      {};

    /*
     * Save the Partner user's
     * permanent account details first.
     *
     * The password created here is the
     * password they will later use at
     * /partner/login.
     */
    const {
      data:
        updatedUserData,

      error:
        updateError,
    } =
      await supabase.auth
        .updateUser({
          password,

          data: {
            ...existingMetadata,

            full_name:
              cleanFullName,

            partner_member_requires_password:
              false,
          },
        });

    if (updateError) {
      throw updateError;
    }

    const updatedUser =
      updatedUserData
        ?.user ||
      currentUser;

    setCurrentUser(
      updatedUser
    );

    setFullName(
      cleanFullName
    );

    setPhase(
      "accepting"
    );

    /*
     * Activate the actual Partner
     * membership only after name and
     * password have been successfully
     * saved.
     */
    const result =
      await acceptPartnerMemberInvitation(
        token
      );

    setContext(
      (current) => ({
        ...(current || {}),

        partner_id:
          result
            ?.partner_id ||
          current
            ?.partner_id,

        partner_name:
          result
            ?.partner_name ||
          current
            ?.partner_name,

        role:
          result?.role ||
          current?.role,
      })
    );

    const acceptedRole =
      String(
        result?.role ||
          context?.role ||
          ""
      )
        .trim()
        .toLowerCase();

    /*
     * Owners may optionally configure
     * their first Partner team before
     * entering the dashboard.
     */
    if (
      acceptedRole ===
      "owner"
    ) {
      setPhase(
        "team-setup"
      );

      return;
    }

    /*
     * Everyone else is finished once
     * their account and membership are
     * ready.
     */
    finishOnboarding();
  } catch (setupError) {
    console.error(
      "Setting up Partner account failed:",
      setupError
    );

    setError(
      setupError?.message ||
        "Your Partner account could not be set up."
    );

    setPhase(
      "account-setup"
    );
  } finally {
    setSavingPassword(
      false
    );
  }
}

  async function handleSignOut() {
    await supabase.auth.signOut();

    setFullName("");
    setCurrentUser(null);
    setSignedInEmail("");
    setError("");
    setLoginLinkSent(false);
    setPhase(
      "authentication-required"
    );
  }

  const passwordIssues =
    getPasswordIssues(
      password
    );

  return (
    <div className="min-h-screen bg-[#F4F4F4] px-5 py-10 text-black sm:px-6">
      <div className="mx-auto w-full max-w-xl">
        <div className="mb-6 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-black text-white">
            <UserCog className="h-7 w-7" />
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-[#D7D7D7] bg-white shadow-sm">
          <header className="border-b border-[#EFEFEF] px-6 py-6 text-center sm:px-8">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#777777]">
              Gwin Partner Portal
            </p>

            <h1 className="mt-2 text-3xl font-black tracking-[-0.03em]">
              Partner team invitation
            </h1>

            {context && (
              <div className="mt-5 rounded-lg border border-[#D7D7D7] bg-[#F4F4F4] p-4 text-left">
                <InfoRow
                  icon={Building2}
                  label="Partner organisation"
                  value={
                    context.partner_name
                  }
                />

                <InfoRow
                  icon={ShieldCheck}
                  label="Partner role"
                  value={
                    ROLE_LABELS[
                      context.role
                    ] ||
                    context.role
                  }
                />

                <InfoRow
                  icon={Mail}
                  label="Invited email"
                  value={
                    context.email_masked
                  }
                />
              </div>
            )}
          </header>

          <main className="px-6 py-7 sm:px-8">
            {(phase === "loading" ||
              phase ===
                "accepting") && (
              <StatusBlock
                icon={Loader2}
                spinning
                title={
                  phase ===
                  "accepting"
                    ? "Activating your partner access"
                    : "Checking your invitation"
                }
                description="Please keep this page open while Gwin securely verifies the invitation."
              />
            )}

            {phase ===
              "authentication-required" && (
              <>
                <StatusBlock
                  icon={ShieldCheck}
                  title="Secure sign-in required"
                  description="Enter the email address that received this invitation. Gwin will email you a secure sign-in link."
                />

                {error && (
                  <ErrorMessage
                    message={error}
                  />
                )}

                {loginLinkSent ? (
  <div className="mt-6 rounded-lg border border-[#D7D7D7] bg-[#F4F4F4] p-5 text-center">
    <Mail className="mx-auto h-7 w-7 text-black" />

    <h2 className="mt-3 font-black text-black">
      Check your email
    </h2>

    <p className="mt-2 text-sm leading-relaxed text-[#777777]">
      Open the newest Gwin
      sign-in email to continue
      accepting your Partner
      invitation.
    </p>
  </div>
) : (
  <form
    onSubmit={
      handleSendLoginLink
    }
    className="mt-6"
  >
    <label
      htmlFor="partner-invite-email"
      className="block text-sm font-black text-black"
    >
      Invited email address
    </label>

    <input
      id="partner-invite-email"
      type="email"
      value={email}
      onChange={(event) =>
        setEmail(
          event.target.value
        )
      }
      disabled={
        sendingLoginLink
      }
      required
      placeholder="team.member@example.com"
      className="mt-2 w-full rounded-lg border border-[#D5D5D5] bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black focus:ring-4 focus:ring-black/10"
    />

    <button
      type="submit"
      disabled={
        sendingLoginLink
      }
      className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-black px-6 py-3 text-sm font-black text-white transition hover:bg-[#252525] disabled:opacity-60"
    >
      <Send className="mr-2 h-4 w-4" />

      {sendingLoginLink
        ? "Sending link..."
        : "Email me a secure link"}
    </button>
        </form>
    )}
  </>
)}

{phase ===
  "account-setup" && (
  <>
    <StatusBlock
      icon={UserCog}
      title="Set up your Partner account"
      description="Confirm your name and create the password you will use to sign in to the Gwin Partner Portal."
    />

    {error && (
      <ErrorMessage
        message={error}
      />
    )}

    <form
      onSubmit={
        handleAccountSetup
      }
      className="mt-6 space-y-5"
    >
      <div>
        <label
          htmlFor="partner-member-full-name"
          className="block text-sm font-black text-black"
        >
          Full name
        </label>

        <input
          id="partner-member-full-name"
          type="text"
          value={fullName}
          onChange={(event) => {
            setFullName(
              event.target.value
            );

            setError("");
          }}
          disabled={
            savingPassword
          }
          required
          maxLength={100}
          autoComplete="name"
          placeholder="e.g. Thabo Mokoena"
          className="mt-2 w-full rounded-lg border border-[#D5D5D5] bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black focus:ring-4 focus:ring-black/10 disabled:bg-[#F4F4F4]"
        />
      </div>

      <PasswordInput
        id="partner-password"
        label="Create password"
        value={password}
        onChange={
          setPassword
        }
        visible={
          showPassword
        }
        onToggle={() =>
          setShowPassword(
            (current) =>
              !current
          )
        }
        disabled={
          savingPassword
        }
      />

      <PasswordInput
        id="partner-confirm-password"
        label="Confirm password"
        value={
          confirmPassword
        }
        onChange={
          setConfirmPassword
        }
        visible={
          showConfirmPassword
        }
        onToggle={() =>
          setShowConfirmPassword(
            (current) =>
              !current
          )
        }
        disabled={
          savingPassword
        }
      />

      <div className="rounded-lg border border-[#D7D7D7] bg-[#F4F4F4] p-4 text-sm">
        <p className="font-black text-black">
          Password requirements
        </p>

        <p className="mt-2 leading-relaxed text-[#777777]">
          At least 8 characters,
          including an uppercase
          letter, lowercase letter,
          number and special
          character.
        </p>

        {password &&
          passwordIssues.length >
            0 && (
            <p className="mt-2 text-xs font-semibold text-[#555555]">
              Still needed:{" "}
              {passwordIssues.join(
                ", "
              )}
            </p>
          )}
      </div>

      {signedInEmail && (
        <div className="rounded-lg border border-[#D7D7D7] bg-white p-4">
          <p className="text-xs font-black uppercase tracking-wider text-[#777777]">
            Account email
          </p>

          <p className="mt-1 break-all text-sm font-bold text-black">
            {signedInEmail}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={
          savingPassword ||
          !fullName.trim()
        }
        className="inline-flex w-full items-center justify-center rounded-lg bg-black px-6 py-3 text-sm font-black text-white transition hover:bg-[#252525] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {savingPassword ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <LockKeyhole className="mr-2 h-4 w-4" />
        )}

        {savingPassword
          ? "Setting up account..."
          : "Continue"}
      </button>
    </form>
  </>
)}

            {phase ===
  "team-setup" && (
  <>
    <StatusBlock
      icon={UserCog}
      title="Add team members"
      description="Invite people from your organisation to help manage your Gwin programme. You can add team members now or skip this step and add them later."
    />

    {error && (
      <ErrorMessage
        message={error}
      />
    )}

    <div className="mt-6 rounded-lg border border-[#D7D7D7] bg-[#F4F4F4] p-5">
      <p className="text-sm font-black text-black">
        Build your Partner team
      </p>

      <p className="mt-2 text-sm leading-relaxed text-[#777777]">
        Add the people who will
        help manage your
        organisation, cohorts and
        entrepreneurs.
      </p>
    </div>

    <div className="mt-6 space-y-3">
      <button
        type="button"
        onClick={
          handleAddTeamMember
        }
        className="w-full rounded-lg bg-black px-6 py-3 text-sm font-black text-white transition hover:bg-[#252525]"
      >
        Add team member
      </button>

      <button
        type="button"
        onClick={
          finishOnboarding
        }
        className="w-full rounded-lg border border-[#D5D5D5] bg-white px-6 py-3 text-sm font-black text-black transition hover:bg-[#F4F4F4]"
      >
        Skip for now
      </button>
    </div>
  </>
)}

{phase ===
  "team-summary" && (
  <>
    <StatusBlock
      icon={CheckCircle2}
      success
      title={
        invitedTeamMembers.length ===
        1
          ? "Team member invited"
          : "Team members invited"
      }
      description="The invitations below have been sent successfully."
    />

    <div className="mt-6 space-y-3">
      {invitedTeamMembers.map(
        (member) => (
          <div
            key={
              member.invitationId ||
              member.email
            }
            className="rounded-lg border border-[#D7D7D7] bg-white p-4"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-black text-black">
                  {member.name ||
                    "Team member"}
                </p>

                <p className="mt-1 break-all text-sm text-[#555555]">
                  {member.email}
                </p>

                <p className="mt-1 text-xs font-semibold text-[#777777]">
                  {ROLE_LABELS[
                    member.role
                  ] ||
                    member.role}
                </p>
              </div>

              <span className="shrink-0 rounded-full bg-[#E9E9E9] px-3 py-1 text-[11px] font-black text-black">
                Invitation sent
              </span>
            </div>
          </div>
        )
      )}
    </div>

    <div className="mt-6 space-y-3">
      <button
        type="button"
        onClick={
          handleAddTeamMember
        }
        className="w-full rounded-lg bg-black px-6 py-3 text-sm font-black text-white transition hover:bg-[#252525]"
      >
        Add another team member
      </button>

      <button
        type="button"
        onClick={
          finishOnboarding
        }
        className="w-full rounded-lg border border-[#D5D5D5] bg-white px-6 py-3 text-sm font-black text-black transition hover:bg-[#F4F4F4]"
      >
        Finish
      </button>
    </div>
  </>
)}

            

            {phase === "success" && (
  <>
    <StatusBlock
      icon={CheckCircle2}
      success
      title="Partner access activated"
      description={`You have joined ${
        context?.partner_name ||
        "the partner organisation"
      } as ${
        ROLE_LABELS[
          context?.role
        ] ||
        context?.role ||
        "a team member"
      }.`}
    />

    <button
      type="button"
      onClick={
        continueToPartnerPortal
      }
      className="mt-6 w-full rounded-lg bg-black px-6 py-3 text-sm font-black text-white transition hover:bg-[#252525]"
    >
      Open Partner Portal
    </button>

    <p className="mt-3 text-center text-xs font-semibold text-[#777777]">
      Redirecting
      automatically...
    </p>
  </>
)}

            {phase ===
              "unavailable" && (
              <StatusBlock
                icon={XCircle}
                danger
                title="Invitation unavailable"
                description={
                  context?.status ===
                  "expired"
                    ? "This invitation has expired. Ask the partner organisation or Gwin administrator to send a fresh invitation."
                    : context?.status ===
                      "revoked"
                    ? "This invitation has been revoked."
                    : "This partner organisation is not currently accepting this invitation."
                }
              />
            )}

            {phase === "error" && (
              <>
                <StatusBlock
                  icon={XCircle}
                  danger
                  title="Invitation could not be accepted"
                  description={
                    error ||
                    "An unexpected error occurred."
                  }
                />

                {signedInEmail && (
                  <div className="mt-6 rounded-2xl border border-[#EFEFEF] p-4">
                    <p className="text-sm text-[#757575]">
                      Currently signed
                      in as:
                    </p>

                    <p className="mt-1 break-all font-black">
                      {signedInEmail}
                    </p>

                    <button
                      type="button"
                      onClick={
                        handleSignOut
                      }
                      className="mt-4 inline-flex items-center rounded-full border border-[#DDDDDD] px-4 py-2 text-sm font-black"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out and retry
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
      <AddTeamMemberModal
  open={teamModalOpen}
  partnerId={
    context?.partner_id
  }
  partnerName={
    context?.partner_name
  }
  actorRole="owner"
  allowOwner
  onClose={() =>
    setTeamModalOpen(false)
  }
  onInvited={
    handleTeamMemberInvited
  }
/>
    </div>
  );
}

function PasswordInput({
  id,
  label,
  value,
  onChange,
  visible,
  onToggle,
  disabled,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-black text-black"
      >
        {label}
      </label>

      <div className="relative mt-2">
        <input
          id={id}
          type={
            visible
              ? "text"
              : "password"
          }
          value={value}
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          disabled={disabled}
          autoComplete="new-password"
          required
          className="w-full rounded-lg border border-[#D5D5D5] bg-white px-4 py-3 pr-12 text-sm text-black outline-none transition focus:border-black focus:ring-4 focus:ring-black/10 disabled:bg-[#F4F4F4]"
        />

        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          aria-label={
            visible
              ? "Hide password"
              : "Show password"
          }
          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#777777] transition hover:text-black disabled:opacity-50"
        >
          {visible ? (
            <EyeOff className="h-5 w-5" />
          ) : (
            <Eye className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-[#D5D5D5] bg-white text-black">
        <Icon className="h-4 w-4" />
      </div>

      <div>
        <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
          {label}
        </p>

        <p className="mt-0.5 font-black">
          {value ||
            "Not available"}
        </p>
      </div>
    </div>
  );
}

function StatusBlock({
  icon: Icon,
  title,
  description,
  spinning = false,
  success = false,
  danger = false,
}) {
  return (
    <div className="text-center">
      <div
        className={[
          "mx-auto flex h-14 w-14 items-center justify-center rounded-xl",
          success
            ? "bg-[#E9E9E9] text-black"
            : danger
            ? "bg-[#E9E9E9] text-black"
            : "bg-black text-white",
        ].join(" ")}
      >
        <Icon
          className={[
            "h-7 w-7",
            spinning
              ? "animate-spin"
              : "",
          ].join(" ")}
        />
      </div>

      <h2 className="mt-5 text-xl font-black text-black">
        {title}
      </h2>

      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#777777]">
        {description}
      </p>
    </div>
  );
}

function ErrorMessage({
  message,
}) {
  return (
    <div
      role="alert"
      className="mt-5 rounded-lg border border-[#BDBDBD] bg-[#F4F4F4] px-4 py-3 text-sm font-semibold text-black"
    >
      {message}
    </div>
  );
}